import { useEffect, useState } from "react";
import api, { fmtINR, fmtDT } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Users, Search, Printer } from "lucide-react";

const STATUS_COLOR = { CREDIT: "bg-amber-100 text-amber-800", PARTIAL: "bg-blue-100 text-blue-800", PENDING: "bg-slate-100 text-slate-700" };

export default function Credits() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    api.get("/credits", { params: { status: "outstanding", limit: 500 } }).then((r) => setRows(r.data));
    api.get("/credits/summary").then((r) => setSummary(r.data)).catch(() => {});
  }, []);

  const filtered = rows.filter((r) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (r.patient_name || "").toLowerCase().includes(s)
      || (r.uhid || "").toLowerCase().includes(s)
      || (r.credit_party_name || "").toLowerCase().includes(s)
      || (r.opd_no || "").toLowerCase().includes(s);
  });

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="font-display text-2xl font-semibold">Credit Ledger</h1>
          <p className="text-sm text-slate-500">All OPDs with outstanding balance — credit parties, partial payments and pending collections in one place.</p>
        </div>
        <button onClick={() => window.print()} data-testid="credits-print" className="border px-3 py-1 text-sm rounded-sm no-print"><Printer className="w-4 h-4 inline mr-1"/>Print</button>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-white border p-3"><div className="label-tiny">Total Outstanding</div><div className="font-display text-xl font-semibold data-mono text-red-800">{fmtINR(summary.total_outstanding)}</div><div className="text-[10px] text-slate-500">{summary.count} entries</div></div>
          <div className="bg-white border p-3"><div className="label-tiny">Credit</div><div className="font-mono text-lg text-amber-800">{fmtINR(summary.by_status.CREDIT || 0)}</div></div>
          <div className="bg-white border p-3"><div className="label-tiny">Partial</div><div className="font-mono text-lg text-blue-800">{fmtINR(summary.by_status.PARTIAL || 0)}</div></div>
          <div className="bg-white border p-3"><div className="label-tiny">Pending</div><div className="font-mono text-lg text-slate-800">{fmtINR(summary.by_status.PENDING || 0)}</div></div>
        </div>
      )}

      <div className="bg-white border p-3 no-print">
        <div className="relative"><Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-slate-400"/>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search patient, UHID, credit party or OPD no" className="pl-7 h-9" data-testid="credits-search"/>
        </div>
      </div>

      <div className="bg-white border">
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 text-left"><th className="label-tiny p-2">Date</th><th className="label-tiny">OPD</th><th className="label-tiny">Patient</th><th className="label-tiny">UHID</th><th className="label-tiny">Doctor</th><th className="label-tiny">Credit Party</th><th className="label-tiny">Contact</th><th className="label-tiny">Due</th><th className="label-tiny">Status</th><th className="label-tiny text-right">Fee</th><th className="label-tiny text-right">Collected</th><th className="label-tiny text-right">Outstanding</th></tr></thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.opd_id} className="border-b" data-testid={`credit-row-${r.opd_no}`}>
                <td className="p-2 text-[11px]">{fmtDT(r.created_at)}</td>
                <td className="font-mono text-xs">{r.opd_no}</td>
                <td className="font-medium">{r.patient_name}</td>
                <td className="font-mono text-xs">{r.uhid}</td>
                <td className="text-xs">{r.doctor_name}</td>
                <td className="text-xs">{r.credit_party_name || "-"}</td>
                <td className="font-mono text-xs">{r.credit_party_contact || "-"}</td>
                <td className="text-xs">{r.credit_due_date || "-"}</td>
                <td><Badge className={`rounded-sm ${STATUS_COLOR[r.payment_status] || ""}`}>{r.payment_status}</Badge></td>
                <td className="text-right font-mono text-xs">{fmtINR(r.fee)}</td>
                <td className="text-right font-mono text-xs text-teal-800">{fmtINR(r.collected)}</td>
                <td className="text-right font-mono text-red-800 font-semibold">{fmtINR(r.outstanding)}</td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan="12" className="p-6 text-center text-slate-500"><Users className="w-8 h-8 mx-auto text-slate-300 mb-2"/>No outstanding credits</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
